import cn from 'classnames'
import React, { ReactElement, useState } from 'react'

import CurrencyDropdown from '@components/CurrencyDropdown'
import BurgerButton from '@components/Header/BurgerButton'
import MenuItem from '@components/Header/Item'
import ManageBookingButton from '@components/Header/ManageBookingButton'
import PersonButton from '@components/Header/PersonButton'
import SignInButton from '@components/Header/SignInButton'
import TrackMyTripButton from '@components/Header/TrackMyTripButton'
import LocaleDropdown from '@components/LocaleDropdown'
import Logo from '@components/Logo'
import SidebarMenu from '@components/SidebarMenu'
import config from '@config'
import { useGEBRetailer } from '@hooks/geb/useGEBRetailer'
import { useGigyaSession } from '@hooks/gigya/useGigyaSession'
import useDistribusionBranding from '@hooks/useDistribusionBranding'
import useIsMobile from '@hooks/useIsMobile'
import { useUserAuth } from '@hooks/userAuthProvider/provider'
import PluginSlot from '@plugins/Slot'
import { useSettings } from '@queries/settings'
import { useParams } from '@stores/params'

import '@components/Header/index.scss'
const isVisible = <T,>(data: T[], mode?: PageMode): boolean => data.length > 1 && mode === 'page'

interface HeaderProps {
  currencyChangeAllowed?: boolean
}

const MobileHeader = ({ currencyChangeAllowed = true }: HeaderProps): ReactElement => {
  const [{ mode, retailerPartnerNumber }] = useParams()
  const [sidebarOpened, setSidebarOpened] = useState(false)
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const [{ currency, locale, manageBookings, logo }] = useSettings()
  const showCurrency = isVisible(currency.supported, mode) && currencyChangeAllowed
  const showLocale = isVisible(locale.supported, mode)
  const isBurgerVisible = showLocale || showCurrency
  const showDistribusionBranding = useDistribusionBranding('header')
  const hasAccountsEnabled = config.accountCreation.byRpn.includes(retailerPartnerNumber)
  const showGEBAccountCreation = useGEBRetailer()
  const { isAuthenticated } = useGigyaSession()
  const { isAuthenticated: isUserAuthenticated } = useUserAuth()

  const showLoginButton = hasAccountsEnabled && !isUserAuthenticated
  return (
    <>
      <SidebarMenu
        opened={sidebarOpened}
        onClose={() => setSidebarOpened(false)}
        showCurrency={showCurrency}
        showLocale={showLocale}
        showManageBookingsLink={manageBookings.enabled}
        showLoginButton={showLoginButton}
        setIsLoginOpen={setIsLoginOpen}
      />
      <div id="header" className="row items-center header__content">
        {isBurgerVisible && (
          <div className="cell-1 burger-button">
            <BurgerButton onClick={() => setSidebarOpened(true)} />
          </div>
        )}
        {mode === 'page' && logo.url && (
          <div
            data-cy="logo-wrapper"
            className={cn(
              {
                'cell-11': !showGEBAccountCreation && !hasAccountsEnabled,
                'cell-8': showGEBAccountCreation && !isAuthenticated,
                'cell-10':
                  (showGEBAccountCreation && isAuthenticated) || (hasAccountsEnabled && !showGEBAccountCreation),
              },
              'cell-sm-9',
              'shrink',
            )}
          >
            <Logo showDistribusionBranding={showDistribusionBranding} url={logo.url} />
          </div>
        )}
        {hasAccountsEnabled && !showGEBAccountCreation && mode !== 'embed' && (
          <div className="cell-1">
            <PersonButton isOpen={isLoginOpen} setIsOpen={setIsLoginOpen} />
          </div>
        )}
        <PluginSlot path="header.right" />
      </div>
    </>
  )
}

const DesktopHeader = ({ currencyChangeAllowed = true }: HeaderProps): ReactElement => {
  const [{ mode, retailerPartnerNumber }] = useParams()
  const [{ currency, locale, manageBookings, menu, trackTrip, logo }] = useSettings()
  const showCurrency = isVisible(currency.supported, mode) && currencyChangeAllowed
  const showLocale = isVisible(locale.supported, mode)
  const showDistribusionBranding = useDistribusionBranding('header')
  const hasAccountsEnabled = config.accountCreation.byRpn.includes(retailerPartnerNumber)

  return (
    <div id="header" className="row items-center">
      {mode === 'page' && (
        <>
          {logo.url && (
            <div className="header-menu-logo mr-6">
              <Logo showDistribusionBranding={showDistribusionBranding} url={logo.url} />
            </div>
          )}
          <div className="header-menu-items items-center">
            {trackTrip.enabled && <TrackMyTripButton />}
            {menu.items.map(item => (
              <div className="cell header-menu" key={item.translationKey}>
                <MenuItem {...item} level={0} />
              </div>
            ))}
          </div>
          <div className="cell"></div>
          <div className="header-menu-actions">
            <div className="header-menu-global">
              {showCurrency && <CurrencyDropdown />}
              {showLocale && <LocaleDropdown />}
            </div>
            {manageBookings.enabled && <ManageBookingButton />}
            {hasAccountsEnabled && <SignInButton />}
            <PluginSlot path="header.right" />
          </div>
        </>
      )}
    </div>
  )
}

const Header = (props: HeaderProps): ReactElement => {
  const isMobile = useIsMobile()

  return isMobile ? <MobileHeader {...props} /> : <DesktopHeader {...props} />
}

export default Header
